import {
  AfterViewInit,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgramacionServicio } from 'src/app/domain/model/agenda/agenda';
import { ESTADO_CITA } from 'src/app/common/constants-ejb';
import {
  ALTURA_MINIMA_CITA_PX,
  HORAS_VISIBLES_DEFAULT,
  HOUR_HEIGHT_DEFAULT_PX,
  alturaHoraAjustada,
  alturaCitaPx,
  alturaSlotPx,
  esCitaCorta,
  offsetHoraPx,
  offsetIndicadorEnSlotPx,
  primeraHoraConCitas,
  repartoHorizontal
} from './week-calendar.utils';

/**
 * Información de un día de la semana para el calendario
 */
export interface DiaSemana {
  fecha: string; // ISO date string YYYY-MM-DD
  nombreDia: string; // "Lunes", "Martes", etc.
  fechaCorta: string; // "15 Mar"
}

/**
 * Representa un slot de 15 minutos en la grilla
 */
interface SlotHorario {
  hora: number; // 0-23
  minuto: number; // 0, 15, 30, 45
}

/**
 * DUMB COMPONENT: WeekCalendar
 *
 * Muestra la grilla semanal con las citas en slots de 15 minutos.
 * Solo presenta datos, no modifica estado ni hace llamadas HTTP.
 *
 * Muestra detalles expandidos de las citas:
 * - Citas de SERVICIO: Clasificación, Cliente, Municipio, etc.
 * - Citas ADMIN: Tipo de cita, Observación, Código
 */
@Component({
  selector: 'app-week-calendar',
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule],
  host: { '[class.wc-v2-host]': 'desktopV2()' },
  styles: [
    `
      .week-calendar-scroll {
        overflow: auto;
        max-height: 540px;
        width: 100%;
      }

      .week-calendar-grid-row {
        display: grid;
        grid-template-columns: 3.5rem repeat(7, minmax(8.5rem, 1fr));
        gap: 0;
        min-width: calc(3.5rem + (7 * 8.5rem));
      }

      .week-calendar-header-row {
        position: sticky;
        top: 0;
      }

      /* === Agenda desktop v2 (>= 1024 px) === */
      :host(.wc-v2-host) {
        display: flex;
        flex-direction: column;
        flex: 1 1 0;
        min-height: 0;
      }

      .wc-v2.week-calendar-scroll {
        flex: 1 1 auto;
        min-height: 0;
        max-height: none;
        overflow-x: hidden;
        overflow-y: auto;
      }

      .wc-v2 .week-calendar-grid-row {
        grid-template-columns: 3rem repeat(7, minmax(0, 1fr));
        min-width: 0;
      }

      .wc-v2 .week-calendar-grid-row:not(.week-calendar-header-row) {
        height: calc(var(--hour-height) / 4);
        box-sizing: border-box;
      }

      .wc-v2 .week-calendar-hour-cell {
        position: relative;
        padding: 0;
        overflow: visible;
      }

      .wc-v2 .wc-hour-label {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        transform: translateY(-50%);
        line-height: 1;
      }

      .wc-v2 .wc-now-line {
        position: absolute;
        left: 0;
        right: 0;
        height: 2px;
        background-color: #dc2626;
        z-index: 15;
        pointer-events: none;
      }

      .wc-v2 .wc-now-line::before {
        content: '';
        position: absolute;
        left: -4px;
        top: -3px;
        width: 8px;
        height: 8px;
        border-radius: 9999px;
        background-color: #dc2626;
      }

      .wc-v2 .wc-cita:focus-visible {
        outline: 2px solid #0033a0;
        outline-offset: -2px;
        z-index: 25;
      }

      .wc-v2 .wc-cita-head {
        padding: 0 3px;
        letter-spacing: -0.2px;
        font-size: 10px;
        line-height: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .wc-v2 .wc-cita-head--corta {
        font-size: 9px;
        line-height: 11px;
        padding: 0 3px;
      }

      .wc-v2 .wc-cita-line {
        font-size: 10px;
        line-height: 12px;
      }

      .wc-v2 .wc-clamp-1 {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        overflow: hidden;
        overflow-wrap: anywhere;
      }

      @media (max-width: 767.98px) {
        .week-calendar-mobile-sticky .week-calendar-hour-header,
        .week-calendar-mobile-sticky .week-calendar-hour-cell {
          position: sticky;
          left: 0;
          background-clip: padding-box;
        }

        .week-calendar-mobile-sticky .week-calendar-hour-header {
          z-index: 40;
        }

        .week-calendar-mobile-sticky .week-calendar-hour-cell {
          z-index: 30;
        }

        .week-calendar-mobile-sticky .week-calendar-day-header {
          position: relative;
          z-index: 20;
        }
      }
    `
  ],
  template: `
    <!-- Navegación de semana (opcional) -->
    @if (showNavigation()) {
      <div class="flex items-center justify-between mb-4 px-4">
        <button
          pButton
          icon="pi pi-chevron-left"
          class="p-button-text min-w-[44px] min-h-[44px]"
          pTooltip="Semana anterior"
          (click)="semanaAnterior.emit()"
        >
          <span class="sr-only">Semana anterior</span>
        </button>
        <span class="font-semibold text-gray-700 dark:text-gray-200">{{
          rangoSemana()
        }}</span>
        <button
          pButton
          icon="pi pi-chevron-right"
          class="p-button-text min-w-[44px] min-h-[44px]"
          pTooltip="Semana siguiente"
          (click)="semanaSiguiente.emit()"
        >
          <span class="sr-only">Semana siguiente</span>
        </button>
      </div>
    }

    <!-- Mensaje si no hay citas -->
    @if (citas().length === 0 && !loading()) {
      <div
        class="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg text-center"
        [ngClass]="desktopV2() ? 'px-3 py-1 mb-2 text-sm' : 'p-4 mb-4'"
      >
        @if (!desktopV2()) {
          <i class="pi pi-calendar text-4xl text-yellow-500 mb-2"></i>
        }
        <p class="text-yellow-800 dark:text-yellow-300">
          Aún no hay citas programadas para este consultor en la semana
          seleccionada
        </p>
      </div>
    }

    <!-- Grid del calendario -->
    <div
      class="week-calendar-scroll"
      [class.week-calendar-mobile-sticky]="enableMobileStickyHours()"
      [class.wc-v2]="desktopV2()"
      [style.--hour-height]="desktopV2() ? alturaHora() + 'px' : null"
      #scrollContainer
    >
      <!-- Header: Horas + Días (sticky para que no se mueva al hacer scroll) -->
      <div
        class="week-calendar-grid-row week-calendar-header-row border-b dark:border-gray-600 z-20"
      >
        <div
          class="week-calendar-hour-header p-2 text-center font-medium text-sm bg-gray-100 dark:bg-gray-700 dark:text-gray-200 border-r border-r-gray-200 dark:border-r-gray-600 shadow-sm"
        ></div>
        @for (dia of diasSemana(); track dia.fecha) {
          <div
            class="week-calendar-day-header text-center font-medium text-sm border-l dark:border-gray-600"
            [ngClass]="[
              desktopV2() ? 'px-1 py-1 leading-tight' : 'p-2',
              esHoyFecha(dia.fecha)
                ? 'bg-blue-100 dark:bg-blue-900'
                : 'bg-gray-100 dark:bg-gray-700 dark:text-gray-200'
            ]"
          >
            <div>{{ dia.nombreDia }}</div>
            <div class="text-xs text-gray-600 dark:text-gray-400">
              {{ dia.fechaCorta }}
            </div>
          </div>
        }
      </div>

      <!-- Grilla de horas (slots de 15 min) -->
      @for (slot of slots; track slotKey(slot)) {
        <div
          class="week-calendar-grid-row"
          [class.border-b]="slot.minuto === 45"
          [class.border-b-gray-300]="slot.minuto === 45"
          [class.dark:border-b-gray-600]="slot.minuto === 45"
        >
          <!-- Hora: solo label en minuto 0 -->
          <div
            class="week-calendar-hour-cell p-1 text-center text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-r border-r-gray-200 dark:border-r-gray-600 shadow-sm"
          >
            @if (slot.minuto === 0) {
              @if (desktopV2()) {
                <span class="wc-hour-label">{{ formatHora(slot.hora) }}</span>
              } @else {
                {{ formatHora(slot.hora) }}
              }
            }
          </div>
          <!-- Celdas por día -->
          @for (dia of diasSemana(); track dia.fecha) {
            @let citasInician = getCitasQueInicianEnSlot(dia.fecha, slot);
            @let hayOcupacion = hayCitaOcupandoSlot(dia.fecha, slot);
            <div
              class="border-l dark:border-gray-600 relative transition-colors"
              [ngClass]="{
                'min-h-[25px]': !desktopV2(),
                'cursor-pointer': !hayOcupacion || citasInician.length > 0,
                'hover:bg-blue-50 dark:hover:bg-blue-900/30': !hayOcupacion,
                'bg-gray-50 dark:bg-gray-800': esHoyFecha(dia.fecha)
              }"
              role="button"
              tabindex="0"
              (click)="onSlotClick(dia.fecha, slot, $event)"
              (keydown)="onSlotKeydown(dia.fecha, slot, $event)"
              [pTooltip]="!hayOcupacion ? tooltipText() : ''"
              tooltipPosition="top"
              [showDelay]="800"
              [hideDelay]="0"
            >
              <!-- Indicador de hora actual (solo v2, semana actual) -->
              @if (desktopV2() && esHoyFecha(dia.fecha) && esSlotActual(slot)) {
                <div
                  class="wc-now-line"
                  aria-hidden="true"
                  [style.top.px]="offsetAhoraPx()"
                ></div>
              }
              <!-- Citas que INICIAN en este slot -->
              @for (
                cita of citasInician;
                track cita.nmconsecutivoProg;
                let i = $index
              ) {
                <div
                  class="absolute mx-1 rounded text-[10px] cursor-pointer shadow-sm border-l-4 flex flex-col z-10 text-gray-800 dark:text-gray-100"
                  [ngClass]="
                    desktopV2() ? 'wc-cita overflow-hidden' : 'left-0 right-0'
                  "
                  [style.background-color]="getLightColor(getCitaColor(cita))"
                  [style.border-left-color]="getCitaColor(cita)"
                  [style.top.px]="0"
                  [style.left.%]="
                    desktopV2()
                      ? repartoCita(i, citasInician.length).left
                      : null
                  "
                  [style.width.%]="
                    desktopV2()
                      ? repartoCita(i, citasInician.length).width
                      : null
                  "
                  [style.height.px]="getCitaHeight(cita)"
                  [pTooltip]="
                    desktopV2() ? getCitaTooltipV2(cita) : getCitaTooltip(cita)
                  "
                  [tooltipEvent]="desktopV2() ? 'both' : 'hover'"
                  [attr.aria-label]="
                    desktopV2() ? getCitaTooltipV2(cita) : null
                  "
                  tooltipPosition="right"
                  role="button"
                  tabindex="0"
                  (click)="citaClick.emit(cita); $event.stopPropagation()"
                  (keydown)="onCitaKeydown(cita, $event)"
                >
                  @if (desktopV2()) {
                    <!-- v2: una sola línea con fecha, horario y código -->
                    <div
                      class="wc-cita-head font-semibold flex-shrink-0"
                      [class.wc-cita-head--corta]="esCitaCorta(cita)"
                      [style.background-color]="getCitaColor(cita)"
                      [style.color]="getContrastTextColor(getCitaColor(cita))"
                    >
                      {{ formatFechaHoraCorta(cita) }} · #{{
                        cita.nmconsecutivoProg
                      }}
                    </div>
                    <div class="flex-1 min-h-0 overflow-hidden px-1">
                      @for (
                        campo of camposVisibles(cita);
                        track campo.etiqueta
                      ) {
                        <div class="wc-cita-line wc-clamp-1">
                          <span class="font-semibold"
                            >{{ campo.etiqueta }}:</span
                          >
                          {{ campo.valor }}
                        </div>
                      }
                    </div>
                  } @else {
                    <!-- Header: Fecha HoraInicio-HoraFin -->
                    <div
                      class="font-semibold px-1 pt-1 text-[9px] flex-shrink-0"
                      [style.background-color]="getCitaColor(cita)"
                      [style.color]="getContrastTextColor(getCitaColor(cita))"
                    >
                      {{ formatHoraCitaCompleta(cita) }}
                    </div>

                    <!-- Contenido según tipo de cita -->
                    <div
                      class="flex-1 overflow-y-auto overflow-x-hidden px-1 pb-1"
                    >
                      @if (cita.dsusoCita === 'SERVICIO') {
                        <div class="leading-tight">
                          <span class="font-semibold">Clasificación:</span>
                          {{ cita.dsclasificacion || '' }}
                        </div>
                        <div class="leading-tight">
                          <span class="font-semibold">Cliente:</span>
                          {{ cita.nombreBeneficiario?.trim() || '' }}
                        </div>
                        <div class="leading-tight">
                          <span class="font-semibold"
                            >Municipio de entrega:</span
                          >
                          {{ cita.dsmunicipioEntrega || '' }}
                        </div>
                        <div class="leading-tight">
                          <span class="font-semibold">Localidad:</span>
                          {{ cita.dslocalidadPpal || '' }}
                        </div>
                        <div class="leading-tight">
                          <span class="font-semibold">Barrio:</span>
                          {{ cita.dsbarrioPpal || '' }}
                        </div>
                        <div class="leading-tight">
                          <span class="font-semibold">Código:</span>
                          {{ cita.nmconsecutivoProg }}
                        </div>
                      } @else {
                        <div class="leading-tight">
                          <span class="font-semibold">Tipo de cita:</span>
                          {{
                            cita.tipoCita?.dstipo ||
                              cita.dstipoCita ||
                              'Sin tipo'
                          }}
                        </div>
                        <div class="leading-tight">
                          <span class="font-semibold">Observación:</span>
                          {{ cita.dsobservaciones || '---' }}
                        </div>
                        <div class="leading-tight">
                          <span class="font-semibold">Código:</span>
                          {{ cita.nmconsecutivoProg }}
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  `
})
export class WeekCalendarComponent implements AfterViewInit {
  // === Inputs ===
  citas = input<ProgramacionServicio[]>([]);
  diasSemana = input<DiaSemana[]>([]);
  loading = input<boolean>(false);
  tooltipText = input<string>(
    'Haga clic en una celda vacía para crear una nueva cita'
  );
  showNavigation = input<boolean>(true);
  enableMobileStickyHours = input<boolean>(false);
  /** Agenda desktop v2 (>= 1024 px): grilla compacta de 7 columnas. */
  desktopV2 = input<boolean>(false);
  /** Altura por hora en px (expuesta como `--hour-height`); solo aplica en v2. */
  hourHeight = input<number>(HOUR_HEIGHT_DEFAULT_PX);
  /** Horas que se ajustan en pantalla (altura por hora automática en v2); 0 usa `hourHeight` fija. */
  horasVisibles = input<number>(HORAS_VISIBLES_DEFAULT);

  // === Outputs ===
  citaClick = output<ProgramacionServicio>();
  celdaClick = output<{ fecha: string; hora: number; minuto: number }>();
  semanaAnterior = output<void>();
  semanaSiguiente = output<void>();

  // === View refs ===
  private readonly scrollContainer =
    viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

  // === Helpers de fecha (evitar problemas de zona horaria) ===

  /**
   * Convierte una fecha a formato YYYY-MM-DD usando hora LOCAL (no UTC).
   * Evita el problema de toISOString() que convierte a UTC y puede dar el día anterior.
   */
  private toLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // === Datos estáticos ===
  /** 96 slots de 15 minutos (24 horas x 4 slots/hora) */
  slots: SlotHorario[] = Array.from({ length: 96 }, (_, i) => ({
    hora: Math.floor(i / 4),
    minuto: (i % 4) * 15
  }));

  /** Altura en px de cada slot de 15 minutos */
  private readonly SLOT_HEIGHT_PX = 25;

  tiposLeyenda = [
    { label: 'Campo', color: '#31B404' },
    { label: 'Informe', color: '#9F81F7' },
    { label: 'Cita Tentativa', color: '#F87431' },
    { label: 'Bloqueo Program', color: '#ADD8E6' },
    { label: 'Administrativas', color: '#F4FA58' },
    { label: 'Cliente Particular', color: '#4287f5' },
    { label: 'Bloqueo Adscritos', color: '#808080' },
    { label: 'EPT Salud', color: '#2E8B57' }
  ];

  // === Computed ===
  rangoSemana = computed(() => {
    const dias = this.diasSemana();
    if (dias.length === 0) {
      return '';
    }
    return `${dias[0].fechaCorta} - ${dias[dias.length - 1].fechaCorta}`;
  });

  /** Altura (px) de un slot de 15 min: 25 fija en v1, hourHeight/4 en v2. */
  /** Altura por hora medida según el espacio disponible (null hasta medir). */
  private readonly alturaAuto = signal<number | null>(null);

  readonly alturaHora = computed(() => this.alturaAuto() ?? this.hourHeight());

  private readonly slotHeightPx = computed(() =>
    this.desktopV2() ? alturaSlotPx(this.alturaHora()) : this.SLOT_HEIGHT_PX
  );

  /** Hora actual, refrescada cada minuto (solo v2) para el indicador. */
  private readonly ahora = signal(new Date());

  /** Semana ya posicionada y si se hizo con citas cargadas (scroll inicial v2). */
  private posicionada: { clave: string; conCitas: boolean } | null = null;

  constructor() {
    // v2: ajusta la altura por hora para que quepan `horasVisibles` en pantalla.
    effect((onCleanup) => {
      const container = this.scrollContainer()?.nativeElement;
      if (this.horasVisibles() <= 0) {
        this.alturaAuto.set(null);
        return;
      }
      if (
        !this.desktopV2() ||
        !container ||
        typeof ResizeObserver === 'undefined'
      ) {
        return;
      }
      const medir = (): void => {
        const header = container.querySelector<HTMLElement>(
          '.week-calendar-header-row'
        );
        this.alturaAuto.set(
          alturaHoraAjustada(
            container.clientHeight - (header?.offsetHeight ?? 0),
            this.horasVisibles()
          )
        );
      };
      const ro = new ResizeObserver(medir);
      ro.observe(container);
      medir();
      onCleanup(() => ro.disconnect());
    });

    effect((onCleanup) => {
      if (!this.desktopV2()) {
        return;
      }
      const id = setInterval(() => this.ahora.set(new Date()), 60_000);
      onCleanup(() => clearInterval(id));
    });

    // Scroll inicial v2: primera hora con citas de la semana (o 07:00).
    effect(() => {
      if (!this.desktopV2()) {
        return;
      }
      const clave = this.diasSemana()[0]?.fecha ?? '';
      const loading = this.loading();
      const citas = this.citas();
      if (loading || !clave) {
        return;
      }
      untracked(() => {
        const previa = this.posicionada;
        if (previa?.clave === clave && previa.conCitas) {
          return;
        }
        this.posicionada = { clave, conCitas: citas.length > 0 };
        setTimeout(() => this.scrollToPrimeraHora(), 50);
      });
    });
  }

  // === Lifecycle ===
  ngAfterViewInit(): void {
    if (this.desktopV2()) {
      return;
    }
    // Esperar al siguiente frame para que el contenedor tenga dimensiones reales.
    requestAnimationFrame(() => this.scrollToCurrentHourCentered());
  }

  /** Método público para re-aplicar scroll (llamado desde el padre cuando el diálogo se abre) */
  scrollToCurrentHour(): void {
    if (this.desktopV2()) {
      setTimeout(() => this.scrollToPrimeraHora(), 100);
      return;
    }
    setTimeout(() => this.scrollToCurrentHourCentered(), 100);
  }

  /** v2: posiciona la grilla en la primera hora con citas de la semana (o 07:00). */
  private scrollToPrimeraHora(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) {
      return;
    }
    const hora = primeraHoraConCitas(
      this.citas(),
      this.diasSemana().map((d) => d.fecha)
    );
    const alto = this.alturaHora();
    // Hasta `horasVisibles` se arranca en 00:00; si no, en la primera cita.
    const top =
      hora < this.horasVisibles()
        ? 0
        : Math.max(0, offsetHoraPx(hora, alto) - alto / 2);
    container.scrollTo({ top });
  }

  private scrollToCurrentHourCentered(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) {
      return;
    }

    const hourRowHeightPx = this.SLOT_HEIGHT_PX * 4; // 4 slots de 15 min = 1 hora
    const currentHour = new Date().getHours();
    const hourTopPx = currentHour * hourRowHeightPx;
    const centeredTop =
      hourTopPx - container.clientHeight / 2 + hourRowHeightPx / 2;
    const maxScrollTop = Math.max(
      0,
      container.scrollHeight - container.clientHeight
    );
    const scrollTop = Math.min(Math.max(0, centeredTop), maxScrollTop);

    container.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }

  // === Helpers de presentación ===
  formatHora(hora: number): string {
    return `${String(hora).padStart(2, '0')}:00`;
  }

  /** Genera key único para trackBy de slots */
  slotKey(slot: SlotHorario): string {
    return `${slot.hora}-${slot.minuto}`;
  }

  /**
   * Formatea la hora de la cita mostrando fecha y rango de horas.
   * Formato: DD/MM/YYYY HH:MM-HH:MM
   */
  formatHoraCitaCompleta(cita: ProgramacionServicio): string {
    if (!cita.feprogramacion) {
      return '';
    }

    const fecha = new Date(cita.feprogramacion);
    const duracionMinutos = Math.round((cita.nmhorasCita || 1) * 60);
    const horaFin = new Date(fecha.getTime() + duracionMinutos * 60000);

    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const anio = fecha.getFullYear();
    const horaInicio =
      fecha.getHours().toString().padStart(2, '0') +
      ':' +
      fecha.getMinutes().toString().padStart(2, '0');
    const horaFinStr =
      horaFin.getHours().toString().padStart(2, '0') +
      ':' +
      horaFin.getMinutes().toString().padStart(2, '0');

    return `${dia}/${mes}/${anio} ${horaInicio}-${horaFinStr}`;
  }

  /**
   * Obtiene las citas que INICIAN en un slot específico de 15 minutos.
   */
  getCitasQueInicianEnSlot(
    fecha: string,
    slot: SlotHorario
  ): ProgramacionServicio[] {
    return this.citas().filter((cita) => {
      if (!cita.feprogramacion) {
        return false;
      }
      const d = new Date(cita.feprogramacion);
      const citaFecha = this.toLocalDateString(d);
      // Redondear minuto de la cita al slot de 15 min más cercano hacia abajo
      const citaMinutoSlot = Math.floor(d.getMinutes() / 15) * 15;
      return (
        citaFecha === fecha &&
        d.getHours() === slot.hora &&
        citaMinutoSlot === slot.minuto
      );
    });
  }

  /**
   * Verifica si hay alguna cita ocupando un slot (aunque no inicie en él).
   */
  hayCitaOcupandoSlot(fecha: string, slot: SlotHorario): boolean {
    const slotMinutos = slot.hora * 60 + slot.minuto;
    return this.citas().some((cita) => {
      if (!cita.feprogramacion) {
        return false;
      }
      const d = new Date(cita.feprogramacion);
      const citaFecha = this.toLocalDateString(d);
      if (citaFecha !== fecha) {
        return false;
      }

      const citaInicioMin = d.getHours() * 60 + d.getMinutes();
      const duracionMin = Math.round((cita.nmhorasCita || 1) * 60);
      const citaFinMin = citaInicioMin + duracionMin;

      return slotMinutos >= citaInicioMin && slotMinutos < citaFinMin;
    });
  }

  /**
   * Verifica si una fecha es hoy.
   */
  esHoyFecha(fechaStr: string): boolean {
    const hoy = this.toLocalDateString(new Date());
    return fechaStr === hoy;
  }

  /**
   * Maneja click en slot del calendario.
   */
  onSlotClick(fecha: string, slot: SlotHorario, event: Event): void {
    event.stopPropagation();
    if (!this.hayCitaOcupandoSlot(fecha, slot)) {
      this.celdaClick.emit({ fecha, hora: slot.hora, minuto: slot.minuto });
    }
  }

  onSlotKeydown(fecha: string, slot: SlotHorario, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.onSlotClick(fecha, slot, event);
  }

  onCitaKeydown(cita: ProgramacionServicio, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.citaClick.emit(cita);
  }

  /**
   * Obtiene el color de una cita según su estado y tipo.
   */
  getCitaColor(cita: ProgramacionServicio): string {
    const esEjecutada =
      cita.cdestado === 'EJE' ||
      cita.cdestado === ESTADO_CITA.EJECUTADA ||
      cita.dsestado?.toUpperCase().includes('EJECUTA');

    if (cita.colorCita?.startsWith('#')) {
      return cita.colorCita;
    }

    if (esEjecutada) {
      if (cita.dscolorCitaEjec?.startsWith('#')) {
        return cita.dscolorCitaEjec;
      }
      if (cita.tipoCita?.dscolorCitaEjec?.startsWith('#')) {
        return cita.tipoCita.dscolorCitaEjec;
      }
    } else {
      if (cita.dscolorCita?.startsWith('#')) {
        return cita.dscolorCita;
      }
      if (cita.tipoCita?.dscolorCita?.startsWith('#')) {
        return cita.tipoCita.dscolorCita;
      }
    }

    if (esEjecutada) {
      return '#28a745';
    }
    if (cita.cdestado === 'CAN' || cita.cdestado === ESTADO_CITA.CANCELADA) {
      return '#dc3545';
    }
    return '#007bff';
  }

  /**
   * Genera un color más claro para el fondo de la cita.
   */
  getLightColor(hexColor: string): string {
    if (!hexColor?.startsWith('#')) {
      return '#f5f5f5';
    }
    return hexColor + '33'; // 20% opacidad
  }

  /**
   * Determina si el texto debe ser claro u oscuro según el fondo.
   */
  getContrastTextColor(hexColor: string): string {
    if (!hexColor?.startsWith('#')) {
      return '#333';
    }

    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#333' : '#fff';
  }

  /**
   * Calcula la altura de una cita en píxeles.
   * Cada slot de 15 min = SLOT_HEIGHT_PX. Una hora = 4 slots.
   */
  getCitaHeight(cita: ProgramacionServicio): number {
    const horas = cita.nmhorasCita ?? 1;
    const totalSlots = horas * 4;
    const alto = totalSlots * this.slotHeightPx() - 2;
    // v2: mínimo legible para una línea de texto (citas de 15 min).
    return this.desktopV2() ? Math.max(alto, ALTURA_MINIMA_CITA_PX) : alto;
  }

  // === Helpers agenda desktop v2 ===

  /** Cita de menos de 30 min: se muestra en una sola línea. */
  esCitaCorta(cita: ProgramacionServicio): boolean {
    return esCitaCorta(cita.nmhorasCita);
  }

  /** Altura de la cita (px) para una altura por hora dada. */
  alturaCita(cita: ProgramacionServicio, alturaHoraPx: number): number {
    return alturaCitaPx(cita.nmhorasCita, alturaHoraPx);
  }

  repartoCita(indice: number, total: number): { left: number; width: number } {
    return repartoHorizontal(indice, total);
  }

  /** true si la hora actual cae en el slot de 15 min indicado. */
  esSlotActual(slot: SlotHorario): boolean {
    const ahora = this.ahora();
    return (
      ahora.getHours() === slot.hora &&
      Math.floor(ahora.getMinutes() / 15) * 15 === slot.minuto
    );
  }

  /** Posición (px) de la línea de hora actual dentro de su slot. */
  offsetAhoraPx(): number {
    return offsetIndicadorEnSlotPx(
      this.ahora().getMinutes(),
      this.alturaHora()
    );
  }

  /** dd/MM HH:MM-HH:MM: fecha y horario compactos para la cabecera de la tarjeta. */
  formatFechaHoraCorta(cita: ProgramacionServicio): string {
    const completa = this.formatHoraCitaCompleta(cita);
    if (!completa) {
      return '';
    }
    const [fecha, rango] = completa.split(' ');
    return `${fecha.substring(0, 5)} ${rango}`;
  }

  /**
   * Campos que caben completos en la tarjeta según su altura (12 px por línea,
   * descontando la cabecera). Nunca corta una línea por la mitad.
   */
  camposVisibles(
    cita: ProgramacionServicio
  ): { etiqueta: string; valor: string }[] {
    const lineas = Math.floor(
      (alturaCitaPx(cita.nmhorasCita, this.alturaHora()) - 14) / 12
    );
    if (lineas <= 0) {
      return [];
    }
    const campos =
      cita.dsusoCita === 'SERVICIO'
        ? [
            {
              etiqueta: 'Cliente',
              valor: cita.nombreBeneficiario?.trim() ?? ''
            },
            { etiqueta: 'Clasificación', valor: cita.dsclasificacion ?? '' },
            { etiqueta: 'Municipio', valor: cita.dsmunicipioEntrega ?? '' },
            { etiqueta: 'Localidad', valor: cita.dslocalidadPpal ?? '' },
            { etiqueta: 'Barrio', valor: cita.dsbarrioPpal ?? '' }
          ]
        : [
            {
              etiqueta: 'Tipo de cita',
              valor: cita.tipoCita?.dstipo || cita.dstipoCita || 'Sin tipo'
            },
            { etiqueta: 'Observación', valor: cita.dsobservaciones || '---' }
          ];
    return campos.slice(0, lineas);
  }

  /** Rango HH:MM-HH:MM de la cita (la fecha ya está en el encabezado del día). */
  formatRangoHoraCita(cita: ProgramacionServicio): string {
    const completa = this.formatHoraCitaCompleta(cita);
    return completa.includes(' ') ? completa.split(' ')[1] : completa;
  }

  /** Tooltip v2: horario completo y código + el detalle de siempre. */
  getCitaTooltipV2(cita: ProgramacionServicio): string {
    return (
      `Horario: ${this.formatHoraCitaCompleta(cita)} - ` +
      `Código: ${cita.nmconsecutivoProg} - ${this.getCitaTooltip(cita)}`
    );
  }

  /**
   * Genera tooltip con información completa de la cita.
   * Replica el formato del tooltip de administrar-agenda.
   */
  getCitaTooltip(cita: ProgramacionServicio): string {
    const fallbackTexto = (
      ...valores: (string | null | undefined)[]
    ): string => {
      for (const valor of valores) {
        if (valor !== undefined && valor !== null && valor !== '') {
          return valor;
        }
      }

      return '';
    };

    const estado = fallbackTexto(cita.dsestado, cita.cdestado, 'Sin estado');
    const tipo = fallbackTexto(
      cita.tipoCita?.dstipo,
      cita.dstipoCita,
      'Sin tipo'
    );
    const obs = fallbackTexto(cita.dsobservaciones, '---');
    const SEPARADOR = ' - ';
    const CLICK_MSG = '(Haga click sobre la cita para modificarla)';

    if (cita.nmconsecOrdenServDetalle || cita.dsusoCita === 'SERVICIO') {
      const clasificacion = fallbackTexto(cita.dsclasificacion);
      const cliente = fallbackTexto(cita.nombreBeneficiario?.trim());
      const municipio = fallbackTexto(cita.dsmunicipioEntrega);
      const localidad = fallbackTexto(cita.dslocalidadPpal, cita.dslocalidad);
      const barrio = fallbackTexto(cita.dsbarrioPpal, cita.dsbarrio);
      const contacto = fallbackTexto(
        cita.dscontactoInfo?.trim(),
        cita.dscontacto,
        cita.dsnombreContacto
      );

      return (
        `Estado de la cita: ${estado}${SEPARADOR}` +
        `Clasificación: ${clasificacion}${SEPARADOR}` +
        `Cliente: ${cliente}${SEPARADOR}` +
        `Municipio de entrega: ${municipio}${SEPARADOR}` +
        `Localidad: ${localidad}${SEPARADOR}` +
        `Barrio: ${barrio}${SEPARADOR}` +
        `Contacto: ${contacto}   ${CLICK_MSG}`
      );
    }

    return (
      `Estado de la cita: ${estado}${SEPARADOR}` +
      `Tipo de cita: ${tipo}${SEPARADOR}` +
      `Nota: ${obs}   ${CLICK_MSG}`
    );
  }
}
